import {
  type AhaEditor,
  type CustomElement,
  type EditorPlugin,
  type EmbeddedWhiteboardElement,
  Guards,
  slate,
} from '@aha-app/aha-editor-v2';
import type WhiteboardContainerController from 'components/whiteboards/WhiteboardContainerController';

interface EmbeddedWhiteboardControllersPluginAPI {
  getWhiteboardController: (
    id: RecordId
  ) => WhiteboardContainerController | undefined;

  setWhiteboardController: (
    id: RecordId,
    controller: WhiteboardContainerController
  ) => void;

  removeWhiteboardController: (id: RecordId) => void;
}

export function whiteboardControllerForEditor(
  ahaEditor: AhaEditor,
  element: CustomElement | undefined
) {
  let whiteboardElement: EmbeddedWhiteboardElement | null = null;

  if (element && Guards.isEmbeddedWhiteboardElement(element)) {
    whiteboardElement = element;
  } else {
    const whiteboards = slate.Editor.above(ahaEditor.editor, {
      match: Guards.isEmbeddedWhiteboardElement,
    });

    if (whiteboards) {
      whiteboardElement = whiteboards[0];
    }
  }

  if (!whiteboardElement) return;
  const whiteboardId = whiteboardElement.whiteboard_id;
  if (!whiteboardId) return;

  const whiteboardPlugin =
    ahaEditor.getPlugin<EmbeddedWhiteboardControllersPlugin>(
      'EmbeddedWhiteboardControllersPlugin'
    );

  return whiteboardPlugin?.api.getWhiteboardController(whiteboardId);
}

export const EmbeddedWhiteboardControllersPluginFactory =
  (): EditorPlugin<EmbeddedWhiteboardControllersPluginAPI> => {
    let whiteboardContainerControllers = new Map<
      RecordId,
      WhiteboardContainerController
    >();

    return {
      id: 'EmbeddedWhiteboardControllersPlugin',
      name: 'Embedded whiteboard controller plugin',
      handlers: {
        onDestroy(ahaEditor) {
          whiteboardContainerControllers = new Map();
        },
      },
      api: {
        getWhiteboardController(id) {
          return whiteboardContainerControllers.get(id);
        },

        setWhiteboardController(id, controller) {
          whiteboardContainerControllers.set(id, controller);
        },

        removeWhiteboardController(id) {
          whiteboardContainerControllers.delete(id);
        },
      },
    };
  };

export type EmbeddedWhiteboardControllersPlugin = ReturnType<
  typeof EmbeddedWhiteboardControllersPluginFactory
>;
